// frontend/src/services/streaming.js — SSE consumer for the tutor's
// streaming endpoint. Used by useTutorRuntime to read incremental
// agent output instead of waiting for a full POST response.
//
// Why fetch + ReadableStream and NOT EventSource:
//   - EventSource only supports GET; we need POST for the request body.
//   - EventSource doesn't let you set Authorization headers (Firebase token).
//   - With fetch we control the request fully; with TextDecoder + a small
//     SSE parser we get the same delivery shape.
//
// Backend wire format (see backend/app/api/routes/tutor_streaming.py):
//   data: {"type": "<mode>", "data": <payload>}\n\n   one event per line group
//   : ping\n\n                                       heartbeat (15s)
//   data: [DONE]\n\n                                 terminator
//
// Caller contract: pass an `onEvent({type, data})` callback. Promise
// resolves when the stream cleanly terminates (DONE or backend close);
// rejects on network/parse failure. Always returns AbortController so
// the caller can cancel in-flight (composer "stop" button hooks here).
import { auth } from '../config/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function buildSSEHeaders() {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream',
  };
  try {
    const user = auth.currentUser;
    if (user) {
      headers['Authorization'] = `Bearer ${await user.getIdToken()}`;
    } else {
      headers['X-User-Id'] = 'anonymous';
    }
  } catch {
    // Continue without auth — backend will reject if it must.
  }
  return headers;
}

async function readErrorDetail(res) {
  let detail = `HTTP ${res.status}`;
  try {
    const text = await res.text();
    if (text) detail = text.slice(0, 300);
  } catch {}
  return detail;
}

function parseSSEFrame(rawEvent) {
  const dataLines = [];
  for (const line of rawEvent.split('\n')) {
    if (line.startsWith(':')) continue;
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  return dataLines.length === 0 ? null : dataLines.join('\n');
}

/**
 * @param {Object} args
 * @param {string} args.path - URL path under API_BASE_URL (e.g. '/api/v1/tutor/message/stream')
 * @param {Object} args.body - JSON body for the POST
 * @param {(evt: {type: string, data: any}) => void} args.onEvent - Per-event callback
 * @returns {{ promise: Promise<void>, abort: () => void }}
 */
export function streamSSE({ path, body, onEvent }) {
  const controller = new AbortController();

  const promise = (async () => {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: await buildSSEHeaders(),
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!res.ok) {
      onEvent({ type: 'error', data: { message: await readErrorDetail(res) } });
      return;
    }
    if (!res.body) {
      onEvent({ type: 'error', data: { message: 'no response body' } });
      return;
    }
    for await (const evt of consumeSSE(res.body, controller.signal)) {
      onEvent(evt);
    }
  })();

  return { promise, abort: () => controller.abort() };
}

function tryParseFrame(rawEvent) {
  const dataStr = parseSSEFrame(rawEvent);
  if (dataStr === null) return { kind: 'skip' };
  if (dataStr === '[DONE]') return { kind: 'done' };
  try {
    return { kind: 'evt', evt: JSON.parse(dataStr) };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('SSE parse error', err, dataStr.slice(0, 200));
    return { kind: 'skip' };
  }
}

async function readChunk(reader, signal) {
  try {
    const { done, value } = await reader.read();
    return { done, value };
  } catch (err) {
    if (signal?.aborted) return { done: true, value: undefined };
    throw err;
  }
}

async function* consumeSSE(body, signal) {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  while (true) {
    const { done, value } = await readChunk(reader, signal);
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const parsed = tryParseFrame(rawEvent);
      if (parsed.kind === 'done') return;
      if (parsed.kind === 'evt') yield parsed.evt;
    }
  }
}

/**
 * Async-generator variant of streamSSE — yields parsed `{type, data}` frames.
 * Useful when the consumer wants `for await` semantics (e.g. plugging into
 * @assistant-ui/react-langgraph's `stream` callback contract).
 *
 * Cancellation: pass an `AbortSignal`. When aborted, the generator returns
 * cleanly (does not throw). Network errors are thrown so callers can decide.
 *
 * @param {Object} args
 * @param {string} args.path
 * @param {Object} args.body
 * @param {AbortSignal} [args.signal]
 * @returns {AsyncGenerator<{type: string, data: any}>}
 */
export async function* streamSSEEvents({ path, body, signal }) {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: await buildSSEHeaders(),
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    yield { type: 'error', data: { message: await readErrorDetail(res) } };
    return;
  }
  if (!res.body) {
    yield { type: 'error', data: { message: 'no response body' } };
    return;
  }
  yield* consumeSSE(res.body, signal);
}
